import { useState, useEffect, useCallback, useMemo } from 'react';

import {
  ContentNugget,
  Major,
  Pagination,
  lottieAnimations,
  majorTransform,
} from '@backrs_ui/common-ui';

import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';

import { Box, Typography, Divider, SxProps, Theme } from '@mui/material';
import Lottie from 'lottie-react';

import { FilterControls } from '../components/FilterControls';
import { MajorBrowseItem, SearchMajorsFilters } from '../types';

/**
 * Props for SearchMajorsPage
 */
interface SearchMajorsPageProps {
  /** List of majors to display */
  majors: MajorBrowseItem[];
  /** Total number of majors (for pagination) */
  total: number;
  /** Current page number */
  page: number;
  /** Raw in-flight fetch flag — drives the "Loading..." text and empty-state suppression. */
  isFetching: boolean;
  /** Delayed loading flag — drives the spinner so fast responses don't flash it. */
  showSpinner: boolean;
  /** Current filter values */
  filters: SearchMajorsFilters;
  /** Callback when filters change */
  onFilterChange: (filters: Partial<SearchMajorsFilters>) => void;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Callback when a major is selected */
  onMajorSelect: (id: number) => void;
  /** Number of items per page */
  perPage: number;
  /** Available college options for filter dropdown */
  collegeOptions: string[];
  /** Callback when Search button is clicked */
  onSearch?: () => void;
  /** Callback when Clear button is clicked */
  onClear?: () => void;
}

/**
 * SearchMajorsPage - Presentational component for browsing majors
 *
 * Features:
 * - FilterControls for search, college filter, and sorting (Figma design)
 * - Responsive grid of MajorCards (4 columns on desktop)
 * - Pagination controls
 */
export const SearchMajorsPage = ({
  majors,
  total,
  page,
  isFetching,
  showSpinner,
  filters,
  onFilterChange,
  onPageChange,
  onMajorSelect,
  perPage,
  collegeOptions,
  onSearch,
  onClear,
}: SearchMajorsPageProps) => {
  // Local state for search input (not debounced - user clicks Search button)
  const [localSearchValue, setLocalSearchValue] = useState(filters.search);

  // Sync local search with URL filters
  useEffect(() => {
    setLocalSearchValue(filters.search);
  }, [filters.search]);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchValue(value);
  }, []);

  const handleSearch = useCallback(() => {
    // Apply local search value to filters
    onFilterChange({ search: localSearchValue });
    onSearch?.();
  }, [localSearchValue, onFilterChange, onSearch]);

  const handleClear = useCallback(() => {
    setLocalSearchValue('');
    onFilterChange({ search: '', colleges: [], sort: 'name', order: 'asc' });
    onClear?.();
  }, [onFilterChange, onClear]);

  const handleCollegeChange = useCallback(
    (values: string[]) => {
      onFilterChange({ colleges: values });
    },
    [onFilterChange],
  );

  const handleSortChange = useCallback(
    (value: SearchMajorsFilters['sort'], order: 'asc' | 'desc') => {
      onFilterChange({ sort: value, order });
    },
    [onFilterChange],
  );

  const handlePrevPage = useCallback(() => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  }, [page, onPageChange]);

  const handleNextPage = useCallback(() => {
    const maxPage = Math.ceil(total / perPage);

    if (page < maxPage) {
      onPageChange(page + 1);
    }
  }, [page, total, perPage, onPageChange]);

  const totalPages = useMemo(() => Math.ceil(total / perPage), [total, perPage]);

  // Convert MajorBrowseItem[] to ContentNugget[] using existing majorTransform
  const nuggets: ContentNugget[] = useMemo(
    () => majors.map((major) => majorTransform(major as Major)),
    [majors],
  );

  const handleNuggetClick = useCallback(
    (nugget: ContentNugget) => {
      onMajorSelect(nugget.id);
    },
    [onMajorSelect],
  );

  return (
    <Box sx={containerSx}>
      {/* Filter Controls */}
      <Box sx={filtersSectionSx}>
        <FilterControls
          searchValue={localSearchValue}
          onSearchChange={handleSearchChange}
          collegeValues={filters.colleges}
          onCollegeChange={handleCollegeChange}
          sortValue={filters.sort}
          orderValue={filters.order}
          onSortChange={handleSortChange}
          collegeOptions={collegeOptions}
          onSearch={handleSearch}
          onClear={handleClear}
          hasActiveSearch={!!filters.search}
        />
      </Box>

      {/* Divider */}
      <Divider sx={dividerSx} />

      {/* Content Area */}
      {showSpinner ? (
        <Box sx={loadingContainerSx}>
          <Lottie animationData={lottieAnimations.dotsDotting} loop />
        </Box>
      ) : majors.length === 0 ? (
        // Suppress the empty state during the loading delay window so it
        // doesn't flash before the spinner appears on a fresh search.
        isFetching ? null : (
          <Box sx={emptyStateSx}>
            <Typography variant="h6" color="text.secondary">
              No majors found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        )
      ) : (
        <>
          {/* Majors Grid */}
          <NuggetCardList nuggets={nuggets} onClick={handleNuggetClick} direction="row" />

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              maxWidth={1200}
              sticky
            />
          )}
        </>
      )}
    </Box>
  );
};

// Styles

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  mb: backrs.spacing['spacing-8xl'],
  '@container (min-width: 900px)': {
    mb: backrs.spacing['spacing-9xl'],
  },
  width: '100%',
  pt: backrs.spacing['spacing-xl'],
});

const filtersSectionSx: SxProps<Theme> = {
  // Inherits container padding
};

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
});

const loadingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: backrs.spacing['spacing-7xl'],
});

const emptyStateSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: backrs.spacing['spacing-7xl'],
  gap: backrs.spacing['spacing-md'],
});
